import { useState } from 'react';
import type { TaskItem, TaskParentType, TaskStatus, Contractor } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'not-started', label: 'Not started' },
  { value: 'in-flight', label: 'In flight' },
  { value: 'complete', label: 'Complete' },
];

interface TaskFormModalProps {
  parentType: TaskParentType;
  parentId: string;
  initial?: TaskItem | undefined;
  /** Siblings under the same parent, excluding this item — the dependsOn candidate pool. */
  siblings: TaskItem[];
  contractors: Contractor[];
  onSaved: (item: TaskItem) => void;
  onCancel: () => void;
  onContractorCreated: (contractor: Contractor) => void;
}

export function TaskFormModal({
  parentType,
  parentId,
  initial,
  siblings,
  contractors,
  onSaved,
  onCancel,
  onContractorCreated,
}: TaskFormModalProps) {
  const [item, setItem] = useState(initial?.item ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [date, setDate] = useState(initial?.date ?? '');
  const [status, setStatus] = useState<TaskStatus>(initial?.status ?? 'not-started');
  const [contractorId, setContractorId] = useState(initial?.contractorId ?? '');
  const [dependsOn, setDependsOn] = useState<string[]>(initial?.dependsOn ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showNewContractor, setShowNewContractor] = useState(false);
  const [newContractorName, setNewContractorName] = useState('');
  const [newContractorPhone, setNewContractorPhone] = useState('');
  const [newContractorTrade, setNewContractorTrade] = useState('');
  const [savingContractor, setSavingContractor] = useState(false);

  async function handleAddContractor() {
    if (!newContractorName.trim()) return;
    setSavingContractor(true);
    try {
      const created = await projectsApi.createContractor({
        name: newContractorName.trim(),
        ...(newContractorPhone ? { phone: newContractorPhone } : {}),
        ...(newContractorTrade ? { trade: newContractorTrade } : {}),
      });
      onContractorCreated(created);
      setContractorId(created.id);
      setShowNewContractor(false);
      setNewContractorName('');
      setNewContractorPhone('');
      setNewContractorTrade('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add contractor');
    } finally {
      setSavingContractor(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!item.trim()) {
      setError('Item is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Editing vs. creating need different "empty field" semantics: on create there's nothing to
      // clear, so an empty field is simply omitted. On update, an emptied field (description
      // cleared, date cleared, contractor set back to "— None —", every dependsOn box unchecked)
      // must be sent as an explicit `null` — omitting the key instead would mean "leave the
      // previous value unchanged," which is the bug this fixes (unchecking every dependsOn box and
      // saving silently no-op'd, since an empty array is falsy-length and got omitted entirely).
      const saved = initial
        ? await projectsApi.updateItem(initial.id, parentType, parentId, {
            item: item.trim(),
            description: description.trim() || null,
            date: date || null,
            status,
            contractorId: contractorId || null,
            dependsOn: dependsOn.length > 0 ? dependsOn : null,
          })
        : await projectsApi.createItem({
            parentType,
            parentId,
            item: item.trim(),
            ...(description.trim() ? { description: description.trim() } : {}),
            ...(date ? { date } : {}),
            status,
            ...(contractorId ? { contractorId } : {}),
            ...(dependsOn.length > 0 ? { dependsOn } : {}),
          });
      onSaved(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 px-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <h2 className="font-serif text-xl font-bold text-phoenix-blue-900">
          {initial ? 'Edit item' : parentType === 'task' ? 'Add subtask' : 'Add task'}
        </h2>

        <form onSubmit={(e) => void handleSubmit(e)} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Item *</label>
            <input
              type="text"
              value={item}
              onChange={(e) => setItem(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              maxLength={200}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              maxLength={4000}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as TaskStatus)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Contractor</label>
            {!showNewContractor ? (
              <div className="mt-1 flex gap-2">
                <select
                  value={contractorId}
                  onChange={(e) => setContractorId(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                >
                  <option value="">— None —</option>
                  {contractors.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.trade ? ` (${c.trade})` : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowNewContractor(true)}
                  className="shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  + New
                </button>
              </div>
            ) : (
              <div className="mt-1 space-y-2 rounded-md border border-gray-200 p-3">
                <input
                  type="text"
                  placeholder="Name *"
                  value={newContractorName}
                  onChange={(e) => setNewContractorName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Trade"
                    value={newContractorTrade}
                    onChange={(e) => setNewContractorTrade(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Phone"
                    value={newContractorPhone}
                    onChange={(e) => setNewContractorPhone(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void handleAddContractor()}
                    disabled={savingContractor || !newContractorName.trim()}
                    className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
                  >
                    {savingContractor ? 'Adding…' : 'Add contractor'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNewContractor(false)}
                    className="text-sm text-gray-500 hover:underline"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {siblings.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Depends on (optional)</label>
              <p className="text-xs text-gray-400">
                Informational only — not enforced. Leave everything unchecked if this item doesn&apos;t
                depend on anything else.
              </p>
              <div className="mt-1 max-h-36 space-y-1 overflow-y-auto rounded-md border border-gray-300 p-2">
                {siblings.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={dependsOn.includes(s.id)}
                      onChange={(e) =>
                        setDependsOn((prev) =>
                          e.target.checked ? [...prev, s.id] : prev.filter((id) => id !== s.id),
                        )
                      }
                    />
                    {s.item}
                  </label>
                ))}
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex items-center gap-2 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-phoenix-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:underline">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
